/**
 * Live Trading V3 — Spacebar Pull All, scoped to the FOCUSED GAME.
 *
 * One keyboard action that removes the trader's resting Kalshi exposure on
 * the game they are aimed at — and nothing else. The dangerous half of that
 * sentence is "and nothing else", so this module deliberately writes no new
 * cancellation logic at all. It is a keyboard entry point into the exact
 * machinery every mouse sweep already uses:
 *
 *   selectExactGameOrders  — the #272 exact-game selection: an order is a
 *     target only when its market resolves through marketStore's fail-closed
 *     canonical-key resolver to the SAME five-part game key. Reads the
 *     authoritative open-order state, never DOM rows, so off-screen and
 *     collapsed orders are included by construction. Anything that ties to
 *     the matchup but cannot be assigned to the exact game BLOCKS the whole
 *     action — cancelling "the easy subset" silently leaves exposure the
 *     trader believes is gone.
 *   freezeCancelSnapshot   — the immutable set the DELETEs are sent from.
 *   executeBulkOrderCancel — the ONE mixed-venue executor, with the #241
 *     cancel fence, per-order results, and retry-failed-only semantics.
 *
 * Boundaries in this release:
 *   • KALSHI ONLY — the hotkey lane is Kalshi-only everywhere else, and the
 *     pull is no wider than the keys that build the exposure. Polymarket
 *     resting orders on the game are NOT touched, and the summary does not
 *     pretend otherwise (the mouse "Cancel this game" sweep remains the
 *     mixed-venue tool).
 *   • Resting orders only. Positions are never flattened.
 *   • PINNED orders are skipped (#943): the trader's pinned Kalshi orders are
 *     subtracted at freeze time and the summary names how many were left.
 *   • One pull at a time: a second Spacebar while one is pending refuses
 *     rather than duplicating cancellation traffic.
 *   • Every pull is AUTHORIZED server-side before any cancel is sent: the
 *     live_pull_all attempt record (game scope, exact frozen targets) is
 *     written first, and the relay refuses it when the LT3_PULL_ALL kill
 *     switch is off or the trader's LT2 grant is revoked. A relay REFUSAL
 *     (any 4xx: 403 kill switch / grant, 400, 401) refuses the WHOLE pull
 *     before a cancel is sent — the packet's separate kill boundary for the
 *     one-key action. B5 (approved Oct 7): when only the audit WRITE fails
 *     (5xx, a timeout, a network error) the pull still cancels, like the
 *     mouse sweeps, and the summary says "audit degraded". A trader pressing
 *     the kill key during a relay hiccup gets their cancels.
 *     Speed-demon D2 (260909): authorization + fan-out are ONE relay call —
 *     `POST /orders/cancel-sequence` with `surface:'live_pull_all'` and the
 *     frozen `targets` (via `accountStore.cancelOrdersByTickers`). The relay
 *     writes the attempt row and awaits it BEFORE any venue write, and its
 *     non-2xx means nothing was sent (`PullAllRefusedError`). A refusal the
 *     relay ANSWERED (kill switch, grant, validation) refuses the pull; its
 *     one pre-execution 5xx — the attempt row could not be written (503) —
 *     is the B5 audit outage: the pull falls back to the per-id DELETEs the
 *     mouse sweeps use and says "audit degraded". On a relay that predates
 *     the merged route (404) or gives no answer, the store falls back to the
 *     standalone authorizer + per-id DELETEs — the same kill boundary, two
 *     round trips; that authorizer applies the same B5 rule.
 *   • D2: the summary counts cancels SENT, never "pulled" (a DELETE the
 *     venue accepted is not proof the order is gone), names the orders NOT
 *     cancelled, and says when Polymarket orders on the same game are still
 *     resting. The caller turns any of those into an amber partial result.
 */
import { useAccountStore } from '@/stores/accountStore';
import { getAuthSessionEpoch, useAuthStore } from '@/stores/authStore';
import { cachedUsFirstOwnAccount } from '@/hooks/useUsAccountPresence';
import { useDisplaySettings } from '@/stores/displaySettings';
import { pinnedKalshiOrderIds, useTrackerPins } from '@/stores/trackerPins';
import {
  selectExactGameOrders,
  freezeCancelSnapshot,
} from '@/lib/trading/gameOrderSelection';
import { executeBulkOrderCancel } from '@/lib/trading/bulkOrderCancel';
import {
  buildPullAllAttemptBody,
  CANCEL_ALL_AUDIT_TIMEOUT_MS,
  PullAllRefusedError,
} from '@/lib/trading/bulkCancelAttemptAudit';
import { apiFetch } from '@/stores/authStore';
import { usePolyRelayAccountStore } from '@/stores/polyRelayAccountStore';
import type { FrozenCancelSnapshot } from '@/lib/trading/gameOrderSelection';
import type { GameRow } from '@/views/ValueDashboard/types';
import type { KalshiOrder } from '@/types/kalshi';

export interface PullAllOutcome {
  status: 'refused' | 'empty' | 'done';
  /** One ack-line sentence — the strip is the trader's only receipt. For a
   *  'done' pull it is the result without the game, which `gameLabel`
   *  carries: "2/2 cancels sent", or "1 NOT cancelled, 1 cancel sent —
   *  check open orders". */
  message: string;
  /** The game the pull was scoped to, e.g. "DAL @ GSV" (empty when the pull
   *  was refused before a game was resolved). */
  gameLabel: string;
  requested: number;
  /** DELETEs the venue accepted. Not proof the orders are gone. */
  submitted: number;
  /** Cancels that failed: these orders are still resting. */
  failed: number;
  /** Orders already being cancelled by an earlier request: neutral. */
  alreadyPending: number;
  /** What went wrong around the cancels, each named for the summary:
   *  'refresh degraded' (open orders were not re-read after), 'audit
   *  degraded' (the attempt record was not written, B5). */
  degraded: string[];
  /** Polymarket orders on the same game that Pull All left resting (it is
   *  Kalshi-only). 0 when none, or when Polymarket orders were never read. */
  polyResting: number;
  /** This login has US access; Pull All does not cancel that venue. No order count inferred. */
  usExcluded?: boolean;
  /** The relay's code on a refused authorization (e.g. PULL_ALL_DISABLED,
   *  LT2_NOT_ENABLED), for the caller to act on. */
  code?: string;
}

function outcome(
  status: PullAllOutcome['status'],
  message: string,
  fields: Partial<PullAllOutcome> = {},
): PullAllOutcome {
  return {
    status,
    message,
    gameLabel: '',
    requested: 0,
    submitted: 0,
    failed: 0,
    alreadyPending: 0,
    degraded: [],
    polyResting: 0,
    ...fields,
  };
}

type Authorization =
  | { kind: 'ok' }
  | { kind: 'refused'; message: string; code?: string }
  | { kind: 'degraded' };

/**
 * The pull's standalone server authorization (the live_pull_all attempt
 * record) — the OLD-RELAY fallback behind the merged cancel-sequence call,
 * recording the same `attemptId` the pull would otherwise have sent in one
 * request. The relay ANSWERING with a refusal (4xx) refuses the pull. A write
 * that fails (5xx), a timeout or a lost connection is an audit failure, not a
 * refusal: the pull proceeds and reports it (B5).
 */
async function authorizePull(
  snapshot: FrozenCancelSnapshot & { scope: 'game' },
  attemptId: string,
): Promise<Authorization> {
  const controller = new AbortController();
  let timeout: ReturnType<typeof setTimeout> | undefined;
  const timedOut = new Promise<'timeout'>((resolve) => {
    timeout = setTimeout(() => {
      controller.abort();
      resolve('timeout');
    }, CANCEL_ALL_AUDIT_TIMEOUT_MS);
  });
  try {
    const res = await Promise.race([
      apiFetch('/api/kalshi/orders/cancel-all-attempt', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(buildPullAllAttemptBody(snapshot, attemptId)),
        signal: controller.signal,
      }),
      timedOut,
    ]);
    if (res === 'timeout') return { kind: 'degraded' };
    if (res.ok) return { kind: 'ok' };
    if (res.status >= 500) return { kind: 'degraded' };
    const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    const error = typeof body.error === 'string' && body.error !== '' ? body.error : null;
    const code = typeof body.code === 'string' && body.code !== '' ? body.code : undefined;
    return {
      kind: 'refused',
      message: error ?? `relay did not authorize the pull (${res.status})`,
      ...(code != null ? { code } : {}),
    };
  } catch {
    return { kind: 'degraded' };
  } finally {
    if (timeout !== undefined) clearTimeout(timeout);
  }
}

/**
 * B5 on the merged route: the ONE non-2xx `cancel-sequence` answers before
 * execution that is not a refusal — its live_pull_all attempt row could not
 * be written (503 "Pull All attempt log unavailable"). Every refusal the
 * relay answers with carries a code (PULL_ALL_DISABLED, LT2_NOT_ENABLED) or
 * is a validation/auth 4xx naming the request; the audit outage carries no
 * code and names the attempt log. The store surfaces both as
 * `PullAllRefusedError`, so the outage is told apart here.
 */
function isAuditOutage(err: PullAllRefusedError): boolean {
  // A code-less 5xx (the lookup failing, or a front proxy's 502/504) is the
  // same class: the relay could not run the pull, it did not refuse it.
  // Every answered refusal carries a code or is a 4xx (main's B5).
  return err.code == null && (/attempt log/i.test(err.message) || (err.status ?? 0) >= 500);
}

/** D2 — Polymarket orders on `game` that a Kalshi-only pull leaves resting,
 *  from the relay-held list every authenticated route keeps current. Never
 *  read means not counted: the summary claims nothing either way. */
function polyRestingOn(game: GameRow, tickerFallback: boolean): number {
  const relay = usePolyRelayAccountStore.getState();
  if (!relay.ordersKnown || relay.orders.length === 0) return 0;
  const poly = selectExactGameOrders({
    game,
    kalshiOrders: [],
    polyOrders: relay.orders,
    tickerFallback,
  });
  return (
    poly.targets.filter((t) => t.venue === 'polymarket').length +
    poly.blockers.filter((b) => b.venue === 'polymarket').length
  );
}

let _pullInFlight = false;

export function isPullAllPending(): boolean {
  return _pullInFlight;
}

export function _resetPullAllForTests(): void {
  _pullInFlight = false;
}

/**
 * `onlyOrders`: sweep exactly these orders instead of the account's open
 * orders, still only where each provably belongs to `game` (the same exact
 * selection by ticker, the same server authorization, the same cancel path).
 * The hotkey listener's second pass uses it for the orders a press reported
 * as accepted after the first pass. They are named by the press itself,
 * because an order the venue just accepted is not in open orders yet (the V2
 * create response carries no status, so it appears only on the next refresh);
 * and an order the trader placed AFTER pressing Pull All is never swept.
 */
export async function executeFocusedGamePull(
  game: GameRow,
  opts: { onlyOrders?: ReadonlyArray<{ orderId: string; ticker: string }> } = {},
): Promise<PullAllOutcome> {
  if (_pullInFlight) {
    return outcome('refused', 'Pull All already pending — wait for the result');
  }

  const account = useAccountStore.getState();
  const tickerFallback = useDisplaySettings.getState().gameCancelTickerFallback;
  const selection = selectExactGameOrders({
    game,
    kalshiOrders:
      opts.onlyOrders != null
        ? opts.onlyOrders.map((o) => ({ orderId: o.orderId, ticker: o.ticker }) as KalshiOrder)
        : account.openOrders ?? [],
    // Kalshi-only in this release — see the module contract above.
    polyOrders: [],
    tickerFallback,
  });
  const gameLabel = selection.gameLabel;

  if (selection.blockers.length > 0) {
    // Fail closed on the WHOLE action: a partially attributable game must
    // not be swept partially. The blocker messages name the orders.
    return outcome(
      'refused',
      `Pull refused — ${selection.blockers.length} order(s) could not be safely assigned to ${gameLabel}`,
      { gameLabel },
    );
  }

  const snapshot = freezeCancelSnapshot({
    scope: 'game',
    label: selection.gameLabel,
    targets: selection.targets,
    pinnedKalshiOrderIds: pinnedKalshiOrderIds(
      useTrackerPins.getState(),
      useAuthStore.getState().user?.id ?? null,
    ),
  });
  const skipped = snapshot.skippedPinned.length;

  // Only the account-wide pass counts other venues; the second pass names
  // exactly one press's orders.
  const polyResting = opts.onlyOrders == null ? polyRestingOn(game, tickerFallback) : 0;
  const usExcluded = opts.onlyOrders == null && cachedUsFirstOwnAccount(getAuthSessionEpoch()) != null;
  const polyHint =
    (polyResting > 0 ? ` · ${polyResting} Polymarket still resting — use Cancel this game` : '')
    + (usExcluded ? ' · Polymarket US is not included — check US open orders' : '');

  if (snapshot.targets.length === 0) {
    return outcome(
      'empty',
      (skipped > 0
        ? `No unpinned resting Kalshi orders on ${gameLabel} (skipped ${skipped} pinned)`
        : `No resting Kalshi orders on ${gameLabel}`) + polyHint,
      { gameLabel, polyResting, ...(usExcluded ? { usExcluded } : {}) },
    );
  }

  // The relay scopes the pull by TICKER and honours the frozen ids inside
  // it; the tickers come from the same authoritative order state the
  // selection read. A target whose ticker cannot be recovered here would
  // silently drop out of the relay's scope — refuse the whole pull instead.
  const tickerByOrderId = new Map<string, string>();
  for (const o of account.openOrders ?? []) {
    if (typeof o.orderId === 'string' && typeof o.ticker === 'string' && o.ticker) {
      tickerByOrderId.set(o.orderId, o.ticker);
    }
  }
  // The second pass names its orders itself (a just-accepted order is not in
  // open orders yet), so their tickers come from the press, not the list.
  for (const o of opts.onlyOrders ?? []) {
    if (o.ticker) tickerByOrderId.set(o.orderId, o.ticker);
  }
  const tickers = new Set<string>();
  // Scope by the FROZEN targets (pins already subtracted, #943), so a ticker
  // that carries only pinned orders never enters the relay's scope.
  for (const t of snapshot.targets) {
    const ticker = tickerByOrderId.get(t.orderId);
    if (!ticker) {
      return outcome(
        'refused',
        `Pull refused — order ${t.orderId} has no market ticker in open-order state`,
        { gameLabel },
      );
    }
    tickers.add(ticker);
  }

  _pullInFlight = true;
  try {
    const attemptId = crypto.randomUUID();
    // SERVER AUTHORIZATION + fan-out in ONE call — the kill boundary lives in
    // the relay's cancel-sequence route: the live_pull_all attempt row is
    // awaited before any venue write, and a refusal (403 from the kill
    // switch or a revoked grant, any other answered 4xx) comes back as
    // PullAllRefusedError with nothing sent. Captured here so the executor's
    // per-order "failed" bookkeeping does not mask a refusal. An audit
    // OUTAGE (the row could not be written) is not obeyed (B5): the cancels
    // go per id, the way the mouse sweeps send them, and the summary says so.
    // Pinned orders (#943) were subtracted at freeze time, so the frozen
    // `targets` the relay honours never include them.
    let refusal: PullAllRefusedError | null = null;
    let auditDegraded = false;
    const report = await executeBulkOrderCancel(snapshot, {
      cancelKalshiOrders: async (ids) => {
        try {
          return await account.cancelOrdersByTickers([...tickers], {
            surface: 'live_pull_all',
            attemptId,
            snapshotId: snapshot.id,
            targets: ids,
            refreshAfter: false,
            // Old relay (404) / no answer: re-establish the boundary through
            // the standalone authorizer before the per-id DELETE fallback.
            // Its B5 rule: an answered refusal throws (nothing sent); an
            // audit write that fails lets the DELETEs proceed, degraded.
            authorizeFallback: async () => {
              const authorization = await authorizePull(snapshot, attemptId);
              if (authorization.kind === 'refused') {
                throw new PullAllRefusedError(authorization.message, authorization.code ?? null);
              }
              if (authorization.kind === 'degraded') auditDegraded = true;
            },
          });
        } catch (err) {
          if (err instanceof PullAllRefusedError && isAuditOutage(err)) {
            auditDegraded = true;
            return await account.cancelOrders([...ids], { refreshAfter: false });
          }
          if (err instanceof PullAllRefusedError) refusal = err;
          throw err;
        }
      },
      // No Polymarket targets can exist in this snapshot (see above); the
      // executor still requires the dep, and reaching it would be a bug.
      requestPolymarketCancel: () => {
        throw new Error('Pull All is Kalshi-only');
      },
      refreshKalshi: () => account.refreshOrders(),
      refreshPolymarket: async () => {},
      // The relay records the attempt inside the merged call.
    });
    if (refusal !== null) {
      const refused = refusal as PullAllRefusedError;
      return outcome(
        'refused',
        `Pull refused — ${refused.message || 'relay did not authorize the pull'}`,
        { gameLabel, ...(refused.code != null ? { code: refused.code } : {}) },
      );
    }

    const submitted = report.submitted.length;
    const failed = report.failed.length;
    const alreadyPending = report.alreadyPending.length;
    const requested = snapshot.targets.length;
    const degraded = [
      ...(report.refreshErrors.length > 0 ? ['refresh degraded'] : []),
      ...(auditDegraded || report.auditError != null ? ['audit degraded'] : []),
    ];
    const parts = [
      failed > 0
        ? `${failed} NOT cancelled, ${submitted} cancel${submitted === 1 ? '' : 's'} sent`
        : `${submitted}/${requested} cancels sent`,
      ...(alreadyPending > 0 ? [`${alreadyPending} already cancelling`] : []),
      ...(skipped > 0 ? [`skipped ${skipped} pinned`] : []),
      ...degraded,
      ...(selection.tickerFallbackTargets > 0
        ? [`${selection.tickerFallbackTargets} identified by Kalshi ticker`]
        : []),
    ];
    const lookHint = failed > 0 || report.refreshErrors.length > 0 ? ' — check open orders' : '';
    return outcome('done', `${parts.join(', ')}${lookHint}${polyHint}`, {
      gameLabel,
      requested,
      submitted,
      failed,
      alreadyPending,
      degraded,
      polyResting,
      ...(usExcluded ? { usExcluded } : {}),
    });
  } finally {
    _pullInFlight = false;
  }
}
